import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Play, RotateCcw, Volume2, VolumeX, ChevronLeft, ChevronRight, Trophy } from "lucide-react";

const W = 440;
const H = 660;
const ROAD_L = 30;
const ROAD_R = W - 30;
const CAR_W = 52;
const CAR_H = 92;
const PLAYER_Y = H - 150;
const ENEMY_COLORS = ["#3B82F6", "#FACC15", "#22C55E", "#A855F7", "#F97316", "#06B6D4"];

const DIFFS = {
  kolay: { label: "KOLAY", base: 3, max: 6.5, ramp: 0.02, spawnBase: 105, spawnMin: 55, mul: 1.25, color: "#22C55E" },
  orta: { label: "ORTA", base: 4, max: 10, ramp: 0.04, spawnBase: 88, spawnMin: 34, mul: 1.5, color: "#F59E0B" },
  zor: { label: "ZOR", base: 6, max: 13, ramp: 0.06, spawnBase: 60, spawnMin: 24, mul: 1.75, color: "#EF4444" },
};

const loadScores = () => {
  try {
    const list = JSON.parse(localStorage.getItem("sistemli-rally-scores") || "[]");
    return Array.isArray(list) ? list.slice(0, 5) : [];
  } catch (e) {
    return [];
  }
};

const freshState = (diff, key) => ({
  px: W / 2 - CAR_W / 2,
  touchX: null,
  enemies: [],
  score: 0,
  speed: diff.base,
  spawn: 50,
  hearts: 3,
  inv: 0,
  flash: 0,
  shake: 0,
  superT: 0,
  dashY: 0,
  d: diff,
  dkey: key,
});

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawCar = (ctx, x, y, w, h, color) => {
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  roundRect(ctx, x + 3, y + 5, w, h, 14);
  ctx.fill();
  ctx.fillStyle = color;
  roundRect(ctx, x, y, w, h, 14);
  ctx.fill();
  ctx.fillStyle = "#111827";
  [[x - 5, y + 12], [x + w - 5, y + 12], [x - 5, y + h - 30], [x + w - 5, y + h - 30]].forEach(
    ([wx, wy]) => {
      roundRect(ctx, wx, wy, 10, 18, 4);
      ctx.fill();
    }
  );
  ctx.fillStyle = "rgba(15,23,42,0.88)";
  roundRect(ctx, x + 9, y + h * 0.16, w - 18, h * 0.24, 9);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  roundRect(ctx, x + 11, y + h * 0.46, w - 22, h * 0.2, 8);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  roundRect(ctx, x + 7, y + h * 0.7, w - 14, 7, 4);
  ctx.fill();
  ctx.fillStyle = "#FDE68A";
  roundRect(ctx, x + 2, y + h * 0.78, 10, 6, 3);
  ctx.fill();
  roundRect(ctx, x + w - 12, y + h * 0.78, 10, 6, 3);
  ctx.fill();
};

const drawHeart = (ctx, x, y, s, filled) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  ctx.beginPath();
  ctx.moveTo(0, 3.2);
  ctx.bezierCurveTo(-5.2, -2.6, -2.4, -7.6, 0, -3.8);
  ctx.bezierCurveTo(2.4, -7.6, 5.2, -2.6, 0, 3.2);
  ctx.fillStyle = filled ? "#EF4444" : "rgba(255,255,255,0.22)";
  ctx.fill();
  ctx.restore();
};

export const RacingGame = () => {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const keysRef = useRef({ left: false, right: false });
  const holdRef = useRef(0);
  const soundRef = useRef(true);
  const audioRef = useRef(null);
  const [screen, setScreen] = useState("start");
  const [finalScore, setFinalScore] = useState(0);
  const [finalLevel, setFinalLevel] = useState("orta");
  const [board, setBoard] = useState([]);
  const [level, setLevel] = useState("orta");
  const [nameInput, setNameInput] = useState("");
  const [saved, setSaved] = useState(false);
  const [sound, setSound] = useState(true);

  useEffect(() => {
    setBoard(loadScores());
  }, []);

  const beep = (freq, dur, type = "square", vol = 0.06) => {
    if (!soundRef.current) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const a = audioRef.current;
      const o = a.createOscillator();
      const g = a.createGain();
      o.type = type;
      o.frequency.value = freq;
      g.gain.value = vol;
      o.connect(g);
      g.connect(a.destination);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.001, a.currentTime + dur / 1000);
      o.stop(a.currentTime + dur / 1000);
    } catch (e) {}
  };

  const startGame = () => {
    stateRef.current = freshState(DIFFS[level], level);
    setSaved(false);
    setScreen("play");
    beep(660, 90, "square", 0.05);
  };

  const saveScore = (e) => {
    e.preventDefault();
    const list = loadScores();
    list.push({
      name: (nameInput.trim() || "İsimsiz").slice(0, 12),
      score: finalScore,
      level: finalLevel,
    });
    list.sort((a, b) => b.score - a.score);
    const top = list.slice(0, 5);
    try {
      localStorage.setItem("sistemli-rally-scores", JSON.stringify(top));
    } catch (err) {}
    setBoard(top);
    setSaved(true);
    beep(920, 140, "triangle", 0.05);
  };

  useEffect(() => {
    if (screen !== "play") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const kd = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a") { keysRef.current.left = true; e.preventDefault(); }
      if (e.key === "ArrowRight" || e.key === "d") { keysRef.current.right = true; e.preventDefault(); }
    };
    const ku = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a") keysRef.current.left = false;
      if (e.key === "ArrowRight" || e.key === "d") keysRef.current.right = false;
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);

    let raf;
    let last = performance.now();
    const loop = (now) => {
      const s = stateRef.current;
      const dt = Math.min((now - last) / 16.7, 2.5);
      last = now;

      s.speed = Math.min(s.d.base + s.score * s.d.ramp, s.d.max);
      const dir = keysRef.current.left ? -1 : keysRef.current.right ? 1 : holdRef.current;
      if (dir !== 0) {
        s.px += dir * 6.2 * dt;
        s.touchX = null;
      } else if (s.touchX !== null) {
        s.px += (s.touchX - CAR_W / 2 - s.px) * Math.min(0.22 * dt, 1);
      }
      s.px = Math.max(ROAD_L + 4, Math.min(ROAD_R - CAR_W - 4, s.px));

      s.dashY = (s.dashY + s.speed * 2.1 * dt) % 64;
      s.spawn -= dt;
      if (s.spawn <= 0) {
        const lane = ROAD_L + 4 + Math.floor(Math.random() * 4) * ((ROAD_R - ROAD_L - CAR_W) / 3);
        s.enemies.push({
          x: lane,
          y: -CAR_H - 12,
          color: ENEMY_COLORS[Math.floor(Math.random() * ENEMY_COLORS.length)],
          counted: false,
        });
        s.spawn = Math.max(s.d.spawnMin, s.d.spawnBase - s.score * 1.6);
      }

      s.enemies.forEach((en) => {
        en.y += s.speed * s.d.mul * dt;
        if (!en.counted && en.y > PLAYER_Y + CAR_H) {
          en.counted = true;
          s.score += 1;
          if (s.score % 5 === 0) { s.superT = 70; beep(880, 120, "triangle", 0.05); }
          else beep(600, 50, "square", 0.035);
        }
      });
      s.enemies = s.enemies.filter((en) => en.y < H + 140);

      if (s.inv > 0) s.inv -= dt;
      if (s.flash > 0) s.flash -= dt;
      if (s.shake > 0) s.shake -= dt;
      if (s.superT > 0) s.superT -= dt;

      for (const en of s.enemies) {
        if (
          s.inv <= 0 &&
          s.px + 6 < en.x + CAR_W - 6 &&
          s.px + CAR_W - 6 > en.x + 6 &&
          PLAYER_Y + 6 < en.y + CAR_H - 6 &&
          PLAYER_Y + CAR_H - 6 > en.y + 6
        ) {
          s.hearts -= 1;
          s.inv = 100;
          s.flash = 14;
          s.shake = 16;
          beep(110, 350, "sawtooth", 0.09);
          if (s.hearts <= 0) {
            setFinalScore(s.score);
            setFinalLevel(s.dkey);
            setScreen("over");
          }
          break;
        }
      }

      ctx.save();
      if (s.shake > 0) ctx.translate((Math.random() - 0.5) * 8, (Math.random() - 0.5) * 8);

      ctx.fillStyle = "#166534";
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#22A14C";
      for (let y = -64; y < H + 64; y += 64) {
        ctx.fillRect(0, y + ((s.dashY / 2) % 64), ROAD_L, 30);
        ctx.fillRect(ROAD_R, y + 32 - ((s.dashY / 2) % 64), ROAD_L, 30);
      }
      ctx.fillStyle = "#374151";
      ctx.fillRect(ROAD_L, 0, ROAD_R - ROAD_L, H);
      ctx.fillStyle = "#F8FAFC";
      ctx.fillRect(ROAD_L, 0, 4, H);
      ctx.fillRect(ROAD_R - 4, 0, 4, H);
      ctx.fillStyle = "#FACC15";
      for (let y = -64 + s.dashY; y < H + 64; y += 64) {
        ctx.fillRect(W / 2 - 3, y, 6, 34);
      }

      s.enemies.forEach((en) => drawCar(ctx, en.x, en.y, CAR_W, CAR_H, en.color));

      if (s.inv <= 0 || Math.floor(s.inv / 6) % 2 === 0) {
        drawCar(ctx, s.px, PLAYER_Y, CAR_W, CAR_H, "#EF4444");
      }

      ctx.fillStyle = "rgba(15,23,42,0.75)";
      roundRect(ctx, 10, 12, W - 20, 52, 14);
      ctx.fill();
      for (let i = 0; i < 3; i++) drawHeart(ctx, 30 + i * 26, 38, 2.2, i < s.hearts);
      ctx.fillStyle = s.d.color;
      ctx.font = "800 11px 'Plus Jakarta Sans', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(s.d.label, W / 2, 24);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "800 24px Outfit, sans-serif";
      ctx.fillText(String(s.score), W / 2, 50);
      ctx.textAlign = "left";

      if (s.superT > 0) {
        ctx.globalAlpha = Math.min(1, s.superT / 30);
        ctx.fillStyle = "#FACC15";
        ctx.font = "800 44px Outfit, sans-serif";
        ctx.textAlign = "center";
        ctx.strokeStyle = "#111827";
        ctx.lineWidth = 7;
        ctx.strokeText("SÜPER!", W / 2, H / 2 - 60);
        ctx.fillText("SÜPER!", W / 2, H / 2 - 60);
        ctx.globalAlpha = 1;
        ctx.textAlign = "left";
      }

      if (s.flash > 0) {
        ctx.fillStyle = `rgba(239,68,68,${(s.flash / 14) * 0.35})`;
        ctx.fillRect(0, 0, W, H);
      }
      ctx.restore();

      if (stateRef.current) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
    };
  }, [screen]);

  const touch = (e) => {
    const s = stateRef.current;
    if (!s || !e.touches[0]) return;
    const rect = canvasRef.current.getBoundingClientRect();
    s.touchX = (e.touches[0].clientX - rect.left) * (W / rect.width);
  };

  const toggleSound = () => {
    setSound((v) => {
      soundRef.current = !v;
      return !v;
    });
  };

  const bestScore = board[0] ? board[0].score : 0;

  return (
    <div className="min-h-screen bg-slate-950 font-body text-white overflow-x-hidden">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(250,204,21,.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(250,204,21,.25) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-lg mx-auto px-4 py-5">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            data-testid="game-home-link"
            className="inline-flex items-center gap-2 rounded-full border-2 border-yellow-400/60 px-4 py-2 text-sm font-bold text-yellow-300 transition-colors hover:bg-yellow-400 hover:text-slate-950"
          >
            <Home className="h-4 w-4" />
            Ana Sayfa
          </Link>
          <div className="flex items-center gap-2 text-sm font-bold text-yellow-300">
            <Trophy className="h-4 w-4" />
            Rekor: {bestScore}
          </div>
          <button
            type="button"
            onClick={toggleSound}
            data-testid="game-sound-toggle"
            aria-label="Sesi aç/kapat"
            className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-yellow-400/60 text-yellow-300 transition-colors hover:bg-yellow-400 hover:text-slate-950"
          >
            {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        </div>

        <h1 className="arcade-title mt-5 text-center font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="game-title">
          SİSTEMLİ RALLY
        </h1>
        <p className="mt-1 text-center text-sm font-semibold text-slate-400">
          Çocuklar için araba yarışı — çarpmadan git, skor yap!
        </p>

        <div
          className="arcade-frame relative mx-auto mt-5 w-full max-w-[440px] overflow-hidden rounded-3xl border-4 border-yellow-400/90 bg-slate-900"
          data-testid="game-frame"
        >
          <canvas
            ref={canvasRef}
            data-testid="game-canvas"
            className="block w-full touch-none select-none"
            style={{ aspectRatio: "440 / 660" }}
            onTouchStart={touch}
            onTouchMove={touch}
            onTouchEnd={() => stateRef.current && (stateRef.current.touchX = null)}
          />

          {screen === "start" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-slate-950/80 px-6 py-8 text-center backdrop-blur-[2px]">
              <span className="arcade-btn inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-lg font-extrabold" data-testid="game-start-badge">
                ATARI MODU
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Zorluk seç</p>
                <div className="mt-2 flex justify-center gap-2">
                  {Object.keys(DIFFS).map((k) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`difficulty-${k}`}
                      onClick={() => setLevel(k)}
                      className={`rounded-full border-2 px-4 py-2 text-sm font-extrabold transition-all ${
                        level === k
                          ? "border-yellow-300 bg-yellow-400 text-slate-950"
                          : "border-slate-600 bg-slate-900/60 hover:border-yellow-400"
                      }`}
                      style={level === k ? {} : { color: DIFFS[k].color }}
                    >
                      {DIFFS[k].label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={startGame}
                data-testid="game-start-btn"
                className="arcade-btn inline-flex items-center gap-2 rounded-full px-10 py-4 text-2xl font-extrabold"
              >
                <Play className="h-6 w-6" />
                BAŞLA
              </button>
              <p className="max-w-xs text-xs font-semibold leading-relaxed text-slate-400">
                Ok tuşları veya ekrana dokunarak arabayı sür. Her geçen araç +1 puan!
              </p>
            </div>
          )}

          {screen === "over" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-slate-950/85 px-6 py-6 text-center backdrop-blur-[2px]">
              <p className="font-display text-3xl font-extrabold text-red-400">Çarptın!</p>
              <p className="font-display text-5xl font-extrabold text-yellow-300" data-testid="game-final-score">
                {finalScore}
              </p>
              <p className="text-sm font-bold text-slate-300">
                puan • {DIFFS[finalLevel].label} • tablo rekoru: {bestScore}
              </p>
              {!saved ? (
                <form onSubmit={saveScore} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    maxLength={12}
                    placeholder="Adını yaz"
                    data-testid="game-name-input"
                    className="w-36 rounded-full border-2 border-slate-600 bg-slate-900 px-4 py-2.5 text-sm font-bold text-white outline-none placeholder:text-slate-500 focus:border-yellow-400"
                  />
                  <button
                    type="submit"
                    data-testid="game-save-score-btn"
                    className="arcade-btn rounded-full px-5 py-2.5 text-sm font-extrabold"
                  >
                    SKORU KAYDET
                  </button>
                </form>
              ) : (
                <p className="text-sm font-extrabold text-emerald-400" data-testid="game-saved-note">
                  Skor tabloya işlendi!
                </p>
              )}
              <button
                type="button"
                onClick={startGame}
                data-testid="game-retry-btn"
                className="arcade-btn inline-flex items-center gap-2 rounded-full px-9 py-4 text-xl font-extrabold"
              >
                <RotateCcw className="h-5 w-5" />
                TEKRAR OYNA
              </button>
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-center gap-4">
          <button
            type="button"
            data-testid="game-left-btn"
            aria-label="Sola git"
            onPointerDown={() => (holdRef.current = -1)}
            onPointerUp={() => (holdRef.current = 0)}
            onPointerLeave={() => (holdRef.current = 0)}
            className="arcade-btn flex h-20 w-28 items-center justify-center rounded-2xl active:translate-y-1"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          <button
            type="button"
            data-testid="game-right-btn"
            aria-label="Sağa git"
            onPointerDown={() => (holdRef.current = 1)}
            onPointerUp={() => (holdRef.current = 0)}
            onPointerLeave={() => (holdRef.current = 0)}
            className="arcade-btn flex h-20 w-28 items-center justify-center rounded-2xl active:translate-y-1"
          >
            <ChevronRight className="h-10 w-10" />
          </button>
        </div>

        <div
          className="mx-auto mt-6 w-full max-w-[440px] rounded-2xl border-2 border-yellow-400/40 bg-slate-900/80 p-4"
          data-testid="score-board"
        >
          <p className="flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-wider text-yellow-300">
            <Trophy className="h-4 w-4" />
            Şerefli Tablo — İlk 5
          </p>
          {board.length === 0 ? (
            <p className="mt-2 text-xs font-semibold text-slate-500">
              Henüz kimse skor yapmadı — ilk sen ol!
            </p>
          ) : (
            <ol className="mt-3 flex flex-col gap-1.5">
              {board.map((r, i) => (
                <li
                  key={`${r.name}-${r.score}-${i}`}
                  data-testid={`score-row-${i + 1}`}
                  className="flex items-center gap-3 rounded-xl bg-slate-800/70 px-3 py-2"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-extrabold ${
                      i === 0
                        ? "bg-yellow-400 text-slate-950"
                        : i === 1
                          ? "bg-slate-300 text-slate-900"
                          : i === 2
                            ? "bg-amber-600 text-white"
                            : "bg-slate-700 text-slate-300"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="flex-1 truncate text-left text-sm font-bold text-white">
                    {r.name}
                  </span>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold"
                    style={{ color: DIFFS[r.level] ? DIFFS[r.level].color : "#94A3B8", background: "rgba(255,255,255,0.06)" }}
                  >
                    {DIFFS[r.level] ? DIFFS[r.level].label : "ORTA"}
                  </span>
                  <span className="font-display text-base font-extrabold text-yellow-300">
                    {r.score}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <p className="mt-4 text-center text-xs font-semibold text-slate-500">
          Sistemli Otomotiv • Aile dostu oyun köşesi
        </p>
      </div>
    </div>
  );
};

export default RacingGame;
